import { useMemo } from "react";
import useSWR, { useSWRConfig } from "swr";

import type { ICompany } from "types/company.types";
import { api } from "utils/axios";
export function useCompanies(teamId?: number) {
	const { mutate: revalidate } = useSWRConfig();

	const endpoint = teamId ? `companies?teamId=${teamId}` : "companies";
	const companies = useSWR<ICompany[]>(endpoint);

	// SWR's <ICompany[]> generic is erased at runtime — the endpoint can hand back
	// a non-array body (e.g. a proxy/gateway HTML or error page served with a 200)
	// which would make `.filter` throw and white-screen the whole app shell. Treat
	// anything that isn't an array as "no data yet" (consumers already handle that).
	const list = Array.isArray(companies.data) ? companies.data : undefined;
	// MEMOIZED, and that is load-bearing rather than an optimisation.
	// `.filter().sort()` builds a NEW array on every render, and `CompanyMenu`
	// puts this array straight into a `useEffect` dependency list. An unstable
	// identity ran that effect ("heal a stale persisted company id") on EVERY
	// render, which — against `CompanyPage`'s effect writing the URL's company
	// id back into the store — became a synchronous store ping-pong that
	// re-rendered the whole tree until React threw "Maximum update depth
	// exceeded" (Sentry LANYAP-WEB-19K).
	const data = useMemo(
		() =>
			list
				?.filter((company) => company.name !== "Draft")
				.sort((a, b) => {
					const name = a.alias || a.name;
					return name.localeCompare(b.alias || b.name);
				}),
		[list],
	);

	const setLastCompany = async (companyId: number) => {
		try {
			await api.post("companies/last", { companyId });
		} catch (error) {
			console.error("Failed to update last company", error);
		}
	};

	return {
		...companies,
		data,
		revalidate: () => revalidate(endpoint),
		setLastCompany,
	};
}

export function useLastCompaniesReviews() {
	const { mutate: revalidate } = useSWRConfig();

	const last = useSWR<ILastReview[]>("review/last");

	return {
		...last,
		revalidate: () => revalidate(`review/last`),
	};
}

interface ILastReview {
	id: number;
	company: {
		id: number;
	};
	createdAt: string;
	updatedAt: string;
	rating: number;
}
